import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  useTheme,
  IconButton,
  styled,
  Stack,
} from "@mui/material";
import { useState, useEffect } from "react";
import {
  FcSimCardChip,
  FcCheckmark,
  FcCancel,
  FcExpired,
} from "react-icons/fc";
import { FiCreditCard, FiCalendar, FiLock } from "react-icons/fi";

const CardLogo = styled("img")({
  position: "absolute",
  right: 16,
  top: "50%",
  transform: "translateY(-50%)",
  height: 24,
});

const CardInput = ({ number, expiry, cvv, onInputChange }) => {
  const theme = useTheme();
  const [cardType, setCardType] = useState("");
  const [errors, setErrors] = useState({ number: "", expiry: "", cvv: "" });

  const cardPatterns = {
    visa: {
      pattern: /^4/,
      logo: "https://www.visa.com/images/partnerlogos/visa_logo.gif",
    },
    mastercard: {
      pattern: /^5[1-5]/,
      logo: "https://www.mastercard.com/mc_us/wc/img/logo/mc-logo-52.svg",
    },
    amex: {
      pattern: /^3[47]/,
      logo: "https://www.americanexpress.com/content/dam/amex/us/merchant/images/merchant-logo.png",
    },
  };

  const detectCardType = (num) => {
    const cleaned = num.replace(/\s/g, "");
    return (
      Object.entries(cardPatterns).find(([_, { pattern }]) =>
        pattern.test(cleaned)
      )?.[0] || ""
    );
  };

  const formatCardNumber = (value) => {
    return value
      .replace(/\D/g, "")
      .slice(0, 16)
      .replace(/(\d{4})(?=\d)/g, "$1 ")
      .trim();
  };

  const formatExpiry = (value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, "");

    // Split into month and year parts
    const month = cleaned.slice(0, 2);
    const year = cleaned.slice(2, 4);

    // Auto-insert slash after month
    let formatted = month;
    if (month.length >= 2) {
      formatted += `/${year}`;
    }

    // Limit to MM/YY format (5 characters max)
    return formatted.slice(0, 5);
  };

  const validateCardNumber = (value) => {
    const cleaned = value.replace(/\s/g, "");
    if (!/^\d+$/.test(cleaned)) return "Invalid characters";
    if (cleaned.length < 13) return "Number too short";
    if (cleaned.length > 16) return "Number too long";
    if (!luhnCheck(cleaned)) return "Invalid card number";
    return "";
  };

  const luhnCheck = (num) => {
    let sum = 0;
    let shouldDouble = false;
    for (let i = num.length - 1; i >= 0; i--) {
      let digit = parseInt(num.charAt(i), 10);
      if (shouldDouble) {
        if ((digit *= 2) > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  };

  const validateExpiry = (value) => {
    const [month, year] = value.split("/").map(Number);
    if (!month || !year) return "Invalid format (MM/YY)";
    if (month < 1 || month > 12) return "Invalid month";

    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    const fullYear = 2000 + year;

    if (
      fullYear < new Date().getFullYear() ||
      (fullYear === new Date().getFullYear() && month < currentMonth)
    ) {
      return "Card expired";
    }
    return "";
  };

  const validateCVV = (value) => {
    if (!/^\d+$/.test(value)) return "Invalid characters";
    const requiredLength = cardType === "amex" ? 4 : 3;
    if (value.length !== requiredLength)
      return `${requiredLength} digits required`;
    return "";
  };

  useEffect(() => {
    setErrors({
      number: validateCardNumber(number),
      expiry: validateExpiry(expiry),
      cvv: validateCVV(cvv),
    });
  }, [number, expiry, cvv, cardType]);

  return (
    <Box
      sx={{
        maxWidth: 400,
        mx: "auto",
        p: 3,
        bgcolor: "background.paper",
        borderRadius: 4,
        boxShadow: 3,
        position: "relative",
        overflow: "hidden",
        "&:before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          bgcolor: theme.palette.primary.main,
        },
      }}
    >
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FcSimCardChip size={32} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment Details
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Card Number"
          value={number}
          onChange={(e) => {
            const formatted = formatCardNumber(e.target.value);
            onInputChange("number", formatted);
            setCardType(detectCardType(formatted));
          }}
          placeholder="•••• •••• •••• ••••"
          error={!!errors.number}
          helperText={errors.number}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FiCreditCard size={20} />
              </InputAdornment>
            ),
            // endAdornment: cardType && (
            //   <CardLogo src={cardPatterns[cardType].logo} alt={cardType} />
            // ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              pr: 6,
              "&.Mui-focused fieldset": {
                borderColor: errors.number ? "error.main" : "primary.main",
              },
            },
          }}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            fullWidth
            label="Expiry Date"
            value={expiry}
            onChange={(e) => {
              const formatted = formatExpiry(e.target.value);
              onInputChange("expiry", formatted);
            }}
            placeholder="MM/YY"
            error={!!errors.expiry}
            helperText={errors.expiry}
            inputProps={{
              maxLength: 5,
              inputMode: "numeric",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiCalendar size={20} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {errors.expiry ? (
                    <FcCancel />
                  ) : expiry.length === 5 ? (
                    <FcCheckmark />
                  ) : null}
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="CVV"
            type="password"
            value={cvv}
            onChange={(e) => {
              const maxLength = cardType === "amex" ? 4 : 3;
              onInputChange("cvv", e.target.value.slice(0, maxLength));
            }}
            placeholder={cardType === "amex" ? "••••" : "•••"}
            error={!!errors.cvv}
            helperText={errors.cvv}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiLock size={20} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {errors.cvv ? (
                    <FcCancel />
                  ) : (
                    cvv.length === (cardType === "amex" ? 4 : 3) && (
                      <FcCheckmark />
                    )
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "text.secondary",
            fontSize: 12,
          }}
        >
          <span>Secure payment processing</span>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FcExpired />
            <span>PCI compliant</span>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default CardInput;
